import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Clock, Star, Award, Users, Car, MapPin, Phone } from 'lucide-react';

export default function AboutPage() {
  const { t } = useLanguage();

  const stats = [
    { icon: Car, value: '50+', label: 'Vehicles' },
    { icon: Users, value: '10K+', label: 'Happy Customers' },
    { icon: Award, value: '15+', label: 'Years Experience' },
    { icon: MapPin, value: '24/7', label: 'Support' },
  ];

  const values = [
    {
      icon: Shield,
      title: t('bestPrices'),
      desc: 'We offer the most competitive rates in the region without compromising on quality.',
    },
    {
      icon: Clock,
      title: t('newCars'),
      desc: 'Our team is available around the clock to assist you with any needs.',
    },
    {
      icon: Star,
      title: t('freeCancel'),
      desc: 'Plans change. Cancel your booking for free up to 24 hours before pickup.',
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=1920)',
          }}
        >
          <div className="absolute inset-0 bg-primary/80"></div>
        </div>
        
        <div className="relative container mx-auto px-4 md:px-8 max-w-7xl text-center text-white">
          <h1 className="font-serif text-4xl md:text-6xl font-bold mb-6">
            {t('aboutTitle')}
          </h1>
          <p className="text-xl text-white/80 max-w-2xl mx-auto">
            {t('aboutDescription')}
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-secondary/30">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-8 h-8 text-accent" />
                </div>
                <span className="block text-4xl font-bold text-gold mb-1">{stat.value}</span>
                <span className="text-muted-foreground">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-bold mb-6">Our Story</h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Founded in Kokkινόχωμα, Greece, Universal Rent a Car has been serving travelers 
                  and locals alike for over 15 years. What started as a small family business 
                  has grown into a trusted name in car rental services.
                </p>
                <p>
                  Our commitment to quality, reliability, and customer satisfaction has earned 
                  us the trust of thousands of customers from around the world. We take pride 
                  in maintaining a modern fleet of well-serviced vehicles to ensure your safety 
                  and comfort.
                </p>
                <p>
                  In addition to our rental services, we also offer professional car wash 
                  facilities, ensuring that every vehicle is spotlessly clean and ready for 
                  your journey.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1560958089-b8a1929cea89?w=600"
                alt="Our team"
                className="rounded-2xl shadow-lg"
              />
              <div className="absolute -bottom-6 -left-6 bg-gold text-primary p-6 rounded-xl shadow-lg">
                <span className="block text-4xl font-bold">15+</span>
                <span className="text-sm font-medium">Years of Excellence</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-secondary/30">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-center mb-12">
            {t('whyChooseUs')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-card border border-border/50 rounded-2xl p-8 card-hover"
                data-testid={`value-${index}`}
              >
                <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-accent" />
                </div>
                <h3 className="font-serif text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-muted-foreground">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="bg-primary text-primary-foreground rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="font-serif text-3xl font-bold mb-4">Visit Us</h2>
                <p className="text-white/70 mb-6">
                  Come see our fleet in person and let us help you find the perfect car for your journey.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-gold" />
                    <span>Kokkινόχωμα, Greece</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gold" />
                    <a href="tel:+306972252180" className="hover:text-gold transition-colors">
                      +30 697 2252180
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gold" />
                    <a href="tel:+306970095042" className="hover:text-gold transition-colors">
                      +30 6970095042
                    </a>
                  </div>
                </div>
              </div>
              <div className="h-64 md:h-80 rounded-xl overflow-hidden">
                <iframe
                  title="Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d24252.05595424645!2d23.5!3d40.8!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDDCsDQ4JzAwLjAiTiAyM8KwMzAnMDAuMCJF!5e0!3m2!1sen!2sgr!4v1"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
